import { captureException } from "@sentry/react";
import { X } from "lucide-react";
import { useMemo, useState } from "react";
import { useStore } from "zustand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TiptapEditor } from "@/components/ui/tiptap-editor";
import { useMentionRoles } from "@/hooks/use-mention-roles";
import { useIsSharedWorkspace } from "@/hooks/use-shared-workspace";
import { toMentionUsers } from "@/lib/mention-users";
import { SENTRY_ENABLED } from "@/lib/sentry";
import { authStore } from "@/lib/stores/auth-store";
import { coreStore } from "@/lib/stores/core-store";
import { uiStore } from "@/lib/stores/ui-store";
import { ChannelView } from "./channel-view";

type ThreadCreationPanelProps = {
	channelId: string;
	replyToMessageId?: string;
};

export function ThreadCreationPanel({
	channelId,
	replyToMessageId,
}: ThreadCreationPanelProps) {
	const [threadName, setThreadName] = useState("");
	const [isCreating, setIsCreating] = useState(false);
	const user = useStore(authStore, (state) => state.user);
	const sendMessage = useStore(coreStore, (state) => state.sendMessage);
	const channels = useStore(coreStore, (state) => state.channels);
	const users = useStore(coreStore, (state) => state.users);
	const setChannelViewOption = useStore(
		uiStore,
		(state) => state.setChannelViewOption
	);

	const shared = useIsSharedWorkspace();
	const mentionUsers = useMemo(
		() => toMentionUsers(users, shared),
		[users, shared]
	);

	const mentionRoles = useMentionRoles(channelId);

	const handleCreateThread = async (
		_html: string,
		richContent: Record<string, unknown>
	) => {
		if (!(threadName.trim() && user?.uid)) return;
		if (isCreating) return;

		setIsCreating(true);
		try {
			await sendMessage({
				channelId,
				authorId: user.uid,
				richContent,
				referencedMessageId: replyToMessageId,
				replyInThread: true,
				threadName: threadName.trim(),
			});

			// TODO: read the new channel off the server response once the procedure
			// returns it. For now, wait for the create broadcast to land.
			await new Promise((resolve) => setTimeout(resolve, 100));

			const threadChannel = Array.from(channels.values()).find(
				(ch) => ch.name === threadName.trim() && ch.parentId === channelId
			);

			if (threadChannel) {
				setChannelViewOption(channelId, (prev) => ({
					...prev,
					sidePanel: {
						type: "thread",
						threadId: threadChannel.id,
					},
				}));
			} else {
				setChannelViewOption(channelId, (prev) => ({
					...prev,
					sidePanel: undefined,
				}));
			}
		} catch (error) {
			console.error("Failed to create thread:", error);
			if (SENTRY_ENABLED)
				captureException(error, { tags: { context: "thread_creation" } });
		} finally {
			setIsCreating(false);
		}
	};

	const handleClose = () => {
		setChannelViewOption(channelId, (prev) => ({
			...prev,
			sidePanel: undefined,
		}));
	};

	return (
		<div className="flex h-full flex-col">
			{/* Header */}
			<ChannelView.Header>
				<ChannelView.HeaderTitle>Create Thread</ChannelView.HeaderTitle>
				<ChannelView.HeaderGroup>
					<Button onClick={handleClose} size="icon" variant="ghost">
						<X className="size-4" />
					</Button>
				</ChannelView.HeaderGroup>
			</ChannelView.Header>

			{/* Content - grows to fill space */}
			<div className="flex-1 overflow-y-auto p-4">
				<div className="text-muted-foreground text-sm">
					Create a new thread to organize your conversation.
				</div>
			</div>

			{/* Bottom inputs - fixed at bottom */}
			<div className="border-t p-4">
				<div className="space-y-3">
					{/* Thread Name Input */}
					<div>
						<label
							className="mb-2 block font-medium text-sm"
							htmlFor="thread-name"
						>
							Thread Name
						</label>
						<Input
							id="thread-name"
							onChange={(e) => setThreadName(e.target.value)}
							placeholder="Enter thread name..."
							value={threadName}
						/>
					</div>

					{/* Message Input */}
					<div>
						<div className="mb-2 block font-medium text-sm">First Message</div>
						<div className="overflow-hidden rounded-lg border bg-background">
							<div className="relative">
								<TiptapEditor
									editorClassName="max-h-[200px] py-3"
									onSubmit={handleCreateThread}
									placeholder="Start the conversation..."
									roles={mentionRoles}
									users={mentionUsers}
								/>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
